import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "../../store";
import { formatCurrency as formatCurrencyFromConstants } from "../../constants/currency";
import {
  selectSelectedCustomer,
  selectCustomerLoading,
  selectCustomerError,
  fetchCustomerById,
  editCustomer,
  removeCustomer,
  selectCustomers
} from "./customerSlice";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Skeleton,
  Stack,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  Edit,
  Delete,
  ArrowBack,
  Business,
  LocationOn,
  Phone,
  Visibility,
  Timeline as TimelineIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { Customer, Site, Order } from "./customer";
import EditCustomerModal from "./EditCustomerModal";
import DetailRow from "../../form/DetailRow";
import SettingsIcon from "@mui/icons-material/Settings";
import PaletteIcon from "@mui/icons-material/Palette";
import { Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import UnifiedTimelineModal from "../../components/timeline/UnifiedTimelineModal";
import { UnifiedTimelineData } from "../../types/timeline";
import customerTimelineService from "../../services/customerTimelineService";



function createData(
  name: string,
  calories: number,
  fat: number,
  carbs: number,
  protein: number
) {
  return { name, calories, fat, carbs, protein };
}

const rows = [
  createData('Frozen yoghurt', 159, 6.0, 24, 4.0),
  createData('Ice cream sandwich', 237, 9.0, 37, 4.3),
  createData('Eclair', 262, 16.0, 24, 6.0),
  createData('Cupcake', 305, 3.7, 67, 4.3),
  createData('Gingerbread', 356, 16.0, 49, 3.9),
];

const CustomerDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const theme = useTheme();

  // Helper function to format currency
  const formatCurrency = (amount: number) => {
    return formatCurrencyFromConstants(amount, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  
  const customer = useAppSelector(selectSelectedCustomer)
  
  const loading = useAppSelector(selectCustomerLoading);
  const error = useAppSelector(selectCustomerError);

  const [orders, setOrders] = useState<Order[]>([]);
  const [editMode, setEditMode] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState<Partial<Customer>>({});
  const [showTimelineModal, setShowTimelineModal] = useState(false);
  const [timelineData, setTimelineData] = useState<UnifiedTimelineData | null>(null);
  const [timelineLoading, setTimelineLoading] = useState(false);


  useEffect(() => {
    if (id) {
      dispatch(fetchCustomerById(id));
    }
  }, [dispatch, id]);
  



  useEffect(() => {
    if (customer) {
      setEditForm({
        name: customer.name,
        email: customer.email,
        phone: customer.phone,
        address: customer.address,
        city: customer.city,
        state: customer.state,
        zipCode: customer.zipCode,
        contactPerson: customer.contactPerson,
        contactPhone: customer.contactPhone,
        contactEmail: customer.contactEmail,
        status: customer.status,
        branchId: customer.branchId,
        type: customer.type,
      });
    }
  }, [customer]);

  // const handleEdit = () => {
  //   setEditMode(true);
  // };

  // useEffect(()=>{
  //   console.log("customer===>",customer.name)
  // },[customer])

  const handleEdit = () => {
    setEditMode(true);
    setIsEditModalOpen(true);
  };

  const handleSave = async () => {
    if (!customer || !id) return;

    try {
      await dispatch(
        editCustomer({ id, customerData: editForm as any })
      ).unwrap();
      setEditMode(false);
    } catch (error) {
      // Error is already handled by the slice
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    if (customer) {
      setEditForm({
        name: customer.name,
        email: customer.email,
        phone: customer.phone,
        address: customer.address,
        city: customer.city,
        state: customer.state,
        zipCode: customer.zipCode,
        contactPerson: customer.contactPerson,
        contactPhone: customer.contactPhone,
        contactEmail: customer.contactEmail,
        status: customer.status,
        branchId: customer.branchId,
        type: customer.type,
      });
    }
  };

  const handleDelete = async () => {
    if (!customer || !id) return;

    try {
      await dispatch(removeCustomer(id)).unwrap();
      navigate("/customers");
    } catch (error) {
      // Error is already handled by the slice
    }
  };

  const handleTimelineView = async () => {
    if (!customer || !id) return;

    setTimelineLoading(true);
    try {
      // Fetch dynamic data from services
      const timelineCustomerData = await customerTimelineService.getCustomerWithTimelineData(id);
      setTimelineData({
        type: 'customer',
        customer: timelineCustomerData,
      });
      setShowTimelineModal(true);
    } catch (error) {
      console.error("Failed to load timeline data:", error);
      // Show error message to user instead of fallback
      alert("Failed to load timeline data. Please try again later.");
    } finally {
      setTimelineLoading(false);
    }
  };

  const getStatusColor = (status: boolean) => {
    return status ? "success" : "error";
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Stack spacing={3}>
          <Skeleton variant="rectangular" height={60} />
          <Skeleton variant="rectangular" height={200} />
          <Skeleton variant="rectangular" height={300} />
        </Stack>
      </Box>
    );
  }

  if (!customer) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Customer not found</Alert>
      </Box>
    );
  }

  // useEffect(()=>{
  //   console.log("+++++++++",customer)
  // },[customer])

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: "flex", alignItems: "center",  }}>
        <IconButton
          onClick={() => navigate("/customers")}
          sx={{ color: "text.secondary" }}
        >
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" component="h1">
          Customer Details
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          onClick={handleTimelineView}
          disabled={timelineLoading}
          startIcon={<TimelineIcon />}
          variant="outlined"
          sx={{ mr: 1 }}
        >
          {timelineLoading ? 'Loading...' : 'Timeline View'}
        </Button>
        <Button
          onClick={handleEdit}
          disabled={editMode}
        >
          <Edit />
        </Button>
        {/* <Button
        color="error"
          onClick={() => setDeleteDialogOpen(true)}
          disabled={editMode}
        >
          <Delete/>
        </Button> */}
        {/* <IconButton
          color="error"
          onClick={() => setDeleteDialogOpen(true)}
          disabled={editMode}
        >
          <Delete />
        </IconButton> */}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
        {/* Customer Profile Card */}
        <Box sx={{ flex: { xs: "1 1 100%", md: "0 0 350px" } }}>
          <Card>            
              <CardContent
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-around",
                  alignItems: "center",
                  p: 3,
                  textAlign: "center",
                }}
              >
                {/* <Badge
                  overlap="circular"
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  // badgeContent={
                  //   <Chip
                  //     label={customer.status ? "Active" : "Inactive"}
                  //     size="small"
                  //     color={getStatusColor(customer.status)}
                  //     sx={{ fontSize: "0.1rem" }}
                  //   />
                  // }
                > */}
                <Box>
                  <Avatar sx={{ width: 100, height: 100, mx: "auto", mb:1 }}>
                    <Business sx={{ fontSize: 60 }} />
                  </Avatar>
                {/* </Badge> */}
                
                <span
                  className={`px-2 py-0.5 text-xs font-medium rounded-full border ${
                    customer.status
                      ? "bg-green-50 text-green-600 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-700"
                      : "bg-red-50 text-red-600 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-700"
                  }`}
                >
                  {customer.status ? "Active" : "Inactive"}
                </span>
                </Box>
                <Box
                  sx={{
                    marginLeft:2,
                    display: "flex",
                    flexDirection: "column",
                    textAlign:"left"
                  }}
                >
                {/* <Typography variant="h5" gutterBottom>
                  {customer?.customer?.name}
                </Typography> */}
                <DetailRow label="" value={customer?.customer?.name}/>
                <DetailRow label="" value={customer?.customer?.customerType}/>
                <DetailRow label="" value={customer?.customer?.phone}/>
                <DetailRow label="" value={customer?.customer?.email}/>

                {/* <Typography variant="body1">{customer?.customer?.customerType}</Typography>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {customer?.customer?.email}
                </Typography>

                {customer.phone && (
                  <Typography variant="body2" color="text.secondary">
                    {customer?.customer?.phone}
                  </Typography>
                )} */}
                </Box> 
              </CardContent>
              <Divider/>
              {/* Details section */}
              <Box textAlign="left" marginLeft={2}>
                {/* <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Address Details:
                </Typography> */}
                <DetailRow label="Address" value={`${customer?.customer?.address || ''}, ${customer?.customer?.city || ''}, ${customer?.customer?.state || ''}`} />
                {/* <Typography variant="body2">
                  <strong>Address:</strong> {customer?.customer?.address || "—"}
                </Typography>
                <Typography variant="body2">
                  <strong>City:</strong> {customer?.customer?.city || "—"}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>State:</strong> {customer?.customer?.state || "—"}
                </Typography> */}

              </Box>             
            
          </Card>
        </Box>
              
        {/* Customer Details */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Summary Cards */}
          {customer?.customer?.sites && (
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
              <Card sx={{ minWidth: 200, flex: 1 }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="primary" gutterBottom>
                    {customer.customer.sites.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Sites
                  </Typography>
                </CardContent>
              </Card>
              
              <Card sx={{ minWidth: 200, flex: 1 }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="secondary" gutterBottom>
                    {customer.customer.sites.reduce((total: number, site: Site) => total + (site.orders?.length || 0), 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Orders
                  </Typography>
                </CardContent>
              </Card>
              
              <Card sx={{ minWidth: 200, flex: 1 }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="success.main" gutterBottom>
                    {customer.customer.sites.reduce((total: number, site: Site) => 
                      total + (site.orders?.filter((order: Order) => order.status === 'completed').length || 0), 0
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Completed Orders
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Sites and Orders Section */}
          {customer?.customer?.sites && customer.customer.sites.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Sites & Orders
                </Typography>
                {customer.customer.sites.map((site: Site) => (               


                    <Accordion elevation={2} sx={{ borderRadius: 1, marginBottom:1}}>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          "& .MuiAccordionSummary-content": {
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                          },
                        }}
                      >
                        {/* <PaletteIcon color="primary" /> */}
                        <Typography fontWeight={600}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" color="primary">
                              {site.name}
                            </Typography>
                            {/* <Chip 
                              label={site.status} 
                              size="small" 
                              color={site.status === 'active' ? 'success' : 'default'}
                              
                            /> */}
                            <span
                              className={`px-2 ml-3 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                site.status
                                  ? "bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100"
                                  : "bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100"
                              }`}
                            >
                              {site.status === 'active' ? 'success' : 'default'}
                            </span>
                          </Box>
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              <LocationOn sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                              {site.address}, {site.city}, {site.state} {site.zipcode}
                            </Typography>
                            {/* <Typography variant="body2" color="text.secondary">
                              <Phone sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                              {site.contactPersonName} - {site.contactMobile}
                            </Typography> */}
                          </Box>
                        </Typography>
                      </AccordionSummary>

                      {/* Orders for this site */}
                    {site.orders && site.orders.length > 0 ? (
                      <AccordionDetails>
                        <Box>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                          Orders ({site.orders.length})
                        </Typography>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Order #</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Priority</TableCell>
                                <TableCell>Progress</TableCell>
                                <TableCell>Total</TableCell>
                                <TableCell>Due Date</TableCell>
                                <TableCell>Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {site.orders.map((order: Order) => (
                                <TableRow key={order.id}>
                                  <TableCell>
                                    <Typography variant="body2" fontWeight="medium">
                                      {order.orderNumber}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={order.status}
                                      size="small"
                                      color={
                                        order.status === "completed" ? "success" : 
                                        order.status === "in_progress" ? "warning" : 
                                        order.status === "dispatched" ? "info" : "default"
                                      }
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={order.priority}
                                      size="small"
                                      color={
                                        order.priority === "urgent" ? "error" : 
                                        order.priority === "high" ? "warning" : 
                                        order.priority === "medium" ? "info" : "default"
                                      }
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography variant="body2">
                                        {order.progressPercentage}%
                                      </Typography>
                                      <Box sx={{ width: 60, bgcolor: 'grey.200', borderRadius: 1, height: 8 }}>
                                        <Box 
                                          sx={{ 
                                            width: `${order.progressPercentage}%`, 
                                            bgcolor: 'primary.main', 
                                            height: '100%', 
                                            borderRadius: 1 
                                          }} 
                                        />
                                      </Box>
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" fontWeight="medium">
                                      {formatCurrencyFromConstants(order.totalAmount || 0)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" color="text.secondary">
                                      {new Date(order.dueDate).toLocaleDateString()}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <IconButton
                                      size="small"
                                      onClick={() => navigate(`/orders/${order.id}`)}
                                    >
                                      <Visibility />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                      </AccordionDetails>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          No orders for this site
                        </Typography>
                      </Box>
                    )}
                      
                    </Accordion>



                   
                  
                ))}
              </CardContent>
            </Card>
          )}

          <Typography variant="h6">Recent Orders</Typography>

          {/* <Accordion elevation={2} sx={{ borderRadius: 2, marginBottom:1}}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                "& .MuiAccordionSummary-content": {
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                },
              }}
            >
              
              <Typography fontWeight={600}>Site Data</Typography>
            </AccordionSummary>

            <AccordionDetails>
              <Box sx={{ display: "flex", justifyContent:"space-between", mb: 1 }}>
                <Typography variant="subtitle1" fontWeight="medium">
                  (Order Number)
                </Typography>
                <Typography variant="subtitle2" color="text.secondary">
                  (Eva Number)
                </Typography> 
                <DetailRow label="Number" value="{Order Number}" />
                <DetailRow label="Eva" value="{Eva Number}" />
                <DetailRow label="Frame" value="{0/4}" />
                <DetailRow label="Panel" value="{5/6}" />
                <DetailRow label="Panel Window" value="{3/6}" />
                <DetailRow label="Status" value="{Pending}" />
                <DetailRow label="Customer" value="{Rajkumar}" />

              </Box>
            </AccordionDetails>
          </Accordion> */}

          
        </Box>
      </Box>
  
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Customer</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {customer.name}? This action cannot
            be undone and will also delete all associated orders.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <EditCustomerModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditMode(false);
        }}
        // selectedCustomer={customer}
        onSubmit={handleSave}
      />

      {/* Timeline Modal */}
      {timelineData && (
        <UnifiedTimelineModal
          isOpen={showTimelineModal}
          onClose={() => {
            setShowTimelineModal(false);
            setTimelineData(null);
          }}
          data={timelineData}
        />
      )}

    </Box>
  );
};

export default CustomerDetails;
